import React from 'react';

// Global ResizeObserver error suppression
// These errors are harmless but noisy and occur when components resize rapidly

const suppressResizeObserverErrors = () => {
  // Store original error handlers
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalWindowError = window.onerror;

  // Check if ResizeObserver errors should be suppressed
  const isResizeObserverError = (message) => {
    if (typeof message !== 'string') return false;
    return message.includes('ResizeObserver loop completed with undelivered notifications') ||
           message.includes('ResizeObserver loop limit exceeded') ||
           message.includes('ResizeObserver') && message.includes('loop');
  };

  // Override console.error
  console.error = (...args) => {
    if (args[0] && isResizeObserverError(args[0])) {
      return; // Suppress ResizeObserver errors
    }
    originalConsoleError.apply(console, args);
  };

  // Override console.warn
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && 
        args[0].includes('ResizeObserver')) {
      return; // Suppress ResizeObserver warnings
    }
    originalConsoleWarn.apply(console, args);
  };

  // Override window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError(message)) {
      return true; // Prevent default error handling
    }
    if (originalWindowError) {
      return originalWindowError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Add global error event listeners
  window.addEventListener('error', (event) => {
    if (isResizeObserverError(event.message)) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true); // Use capture phase

  // Suppress unhandled promise rejections related to ResizeObserver
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message && isResizeObserverError(event.reason.message)) {
      event.preventDefault();
      return false;
    }
  });

  // Suppress ResizeObserver errors in React's error boundary
  const originalGetDerivedStateFromError = React.Component.getDerivedStateFromError;
  if (originalGetDerivedStateFromError) {
    React.Component.getDerivedStateFromError = (error) => {
      if (error && error.message && isResizeObserverError(error.message)) {
        return null; // Don't update state for ResizeObserver errors
      }
      return originalGetDerivedStateFromError.call(this, error);
    };
  }

  // Additional ResizeObserver error suppression for modern browsers
  if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            if (!isResizeObserverError(error.message)) {
              console.error('ResizeObserver callback error:', error);
            }
          }
        });
      }
    };
  }
};

export default suppressResizeObserverErrors;
